import {
  ArrowRight,
  Check,
  CircleDot,
  Copy,
  EyeOff,
  Info,
  OctagonAlert,
  ShieldAlert,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { Finding, Report } from "../core/analysis";
import type { AuditEvent, Severity } from "../core/events";
import {
  flowParts,
  type ManifestBlock,
  manifestBlocks,
  REPORT_SEVERITIES,
  severityCounts,
  splitFindings,
} from "../core/report";
import {
  CATEGORY_LABELS,
  describeEvent,
  formatSite,
  type LayerNode,
  layerLabel,
  layerTree,
  type RunStatus,
} from "../core/timeline";

/** Literal class names, so Tailwind can see them. Color is never the only cue. */
const SEVERITY_STYLE: Record<Severity, string> = {
  critical: "text-red border-red",
  suspicious: "text-orange border-orange",
  notable: "text-blue border-blue",
  info: "text-comment border-comment",
};

const SEVERITY_ICON: Record<Severity, typeof Info> = {
  critical: OctagonAlert,
  suspicious: TriangleAlert,
  notable: Info,
  info: CircleDot,
};

interface ReportPaneProps {
  report: Report | null;
  status: RunStatus;
  /** The (escalated) log, to describe evidence. */
  events: readonly AuditEvent[];
  /** Jump to an evidence event: its timeline row and its source line. */
  onEvidence: (seq: number) => void;
  /** Open a code layer's editor tab. */
  onOpenLayer: (layer: number) => void;
}

/**
 * The Report tab (docs/ui-design.md §3): everything here is derived from the
 * UI store by core/analysis; this component only lays it out.
 */
export function ReportPane({ report, status, events, onEvidence, onOpenLayer }: ReportPaneProps) {
  if (!report) {
    return (
      <p data-testid="report" className="px-3 py-2 text-comment">
        {status === "running"
          ? "The report is ready when the run finishes."
          : "Run the code to see its report."}
      </p>
    );
  }
  const bySeq = new Map(events.map((e) => [e.seq, e]));
  const { findings, evasion } = splitFindings(report.findings);
  const evidence = (seqs: readonly number[]) => (
    <EvidenceLinks seqs={seqs} bySeq={bySeq} onEvidence={onEvidence} />
  );
  return (
    <div data-testid="report" className="space-y-4 px-3 py-3 text-sm">
      <Section id="summary" title="Summary">
        <SeverityCounts findings={report.findings} />
        {report.manifest.summary.length > 0 ? (
          <p data-testid="summary-text" className="mt-2 leading-relaxed text-fg">
            {report.manifest.summary.join(" ")}
          </p>
        ) : (
          <p data-testid="summary-text" className="mt-2 text-comment">
            This run used no capabilities Afterimage records.
          </p>
        )}
      </Section>

      <Section id="findings" title="Findings">
        {findings.length === 0 ? (
          <Empty>No findings.</Empty>
        ) : (
          <ol className="space-y-2">
            {findings.map((f) => (
              <FindingCard key={`${f.rule}:${f.title}`} finding={f} evidence={evidence} />
            ))}
          </ol>
        )}
      </Section>

      <Section id="flows" title="Data flows">
        {report.flows.length === 0 ? (
          <Empty>No planted canary reached a network sink.</Empty>
        ) : (
          <ol className="space-y-1 font-mono text-[13px]">
            {report.flows.map((flow) => {
              const parts = flowParts(flow);
              return (
                <li
                  key={`${flow.canary}:${flow.seq}`}
                  data-testid="flow"
                  className="flex flex-wrap items-center gap-2"
                >
                  <span className="text-cat-storage">{parts.source}</span>
                  <span className="flex items-center gap-1 text-comment">
                    ──{parts.via}
                    <ArrowRight aria-label="sent to" className="size-3.5" />
                  </span>
                  <span className="text-red">{parts.destination}</span>
                  {evidence([flow.seq])}
                </li>
              );
            })}
          </ol>
        )}
      </Section>

      <Section id="layers" title="Code layers">
        {report.layers.length === 0 ? (
          <Empty>No dynamic code was revealed.</Empty>
        ) : (
          <LayerTree nodes={layerTree([...report.layers])} onOpenLayer={onOpenLayer} />
        )}
      </Section>

      <Section id="manifest" title="Permission manifest">
        <ManifestBlocks blocks={manifestBlocks(report.manifest)} />
      </Section>

      <Section id="evasion" title="Evasion & tampering">
        {evasion.length === 0 ? (
          <Empty>No sandbox-detection attempts or hooked APIs were observed.</Empty>
        ) : (
          <ol className="space-y-2">
            {evasion.map((f) => (
              <FindingCard key={`${f.rule}:${f.title}`} finding={f} evidence={evidence} />
            ))}
          </ol>
        )}
      </Section>

      <Section id="coverage" title="Coverage caveats">
        <p className="mb-2 text-fg-dark">
          Dynamic analysis only shows what this run did. Branches it didn't take can hide more
          behavior, so there is no overall verdict.
        </p>
        {report.coverage.length > 0 && (
          <ul className="space-y-2">
            {report.coverage.map((note, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: notes can repeat an id
                key={`${note.id}:${i}`}
                data-testid="coverage-note"
                data-note={note.id}
                className="rounded border border-border bg-bg-dark p-2"
              >
                <p className="flex items-start gap-1.5 font-medium">
                  <EyeOff aria-hidden className="mt-0.5 size-3.5 shrink-0 text-comment" />
                  {note.title}
                </p>
                <p className="mt-1 text-fg-dark">{note.detail}</p>
                {note.evidence.length > 0 && evidence(note.evidence)}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={`report-${id}`} data-testid={`report-${id}`}>
      <h3
        id={`report-${id}`}
        className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-fg-dark"
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

const Empty = ({ children }: { children: ReactNode }) => <p className="text-comment">{children}</p>;

function SeverityBadge({
  severity,
  muted = false,
  children,
}: {
  severity: Severity;
  /** Grey it out (a zero count). */
  muted?: boolean;
  children?: ReactNode;
}) {
  const Icon = SEVERITY_ICON[severity];
  const style = muted ? "text-comment border-border" : SEVERITY_STYLE[severity];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 text-[11px] font-semibold ${style}`}
    >
      <Icon aria-hidden className="size-3" />
      {children ?? severity}
    </span>
  );
}

function SeverityCounts({ findings }: { findings: readonly Finding[] }) {
  const counts = severityCounts(findings);
  return (
    <ul aria-label="Findings by severity" className="flex flex-wrap gap-2">
      {REPORT_SEVERITIES.map((severity) => (
        <li key={severity} data-testid={`count-${severity}`}>
          <SeverityBadge severity={severity} muted={counts[severity] === 0}>
            {counts[severity]} {severity}
          </SeverityBadge>
        </li>
      ))}
    </ul>
  );
}

function FindingCard({
  finding,
  evidence,
}: {
  finding: Finding;
  evidence: (seqs: readonly number[]) => ReactNode;
}) {
  const Icon = finding.rule.startsWith("tamper.") ? ShieldAlert : null;
  return (
    <li
      data-testid="finding"
      data-rule={finding.rule}
      data-severity={finding.severity}
      className={`rounded border-l-2 bg-bg-dark p-2 ${SEVERITY_STYLE[finding.severity].split(" ")[1]}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <SeverityBadge severity={finding.severity} />
        {Icon && <Icon aria-hidden className="size-3.5 text-red1" />}
        <span className="min-w-0 flex-1 font-medium text-fg">{finding.title}</span>
        <code className="text-[11px] text-comment">{finding.rule}</code>
      </div>
      <p className="mt-1 text-fg-dark">{finding.explanation}</p>
      {finding.evidence.length > 0 && evidence(finding.evidence)}
    </li>
  );
}

/** How many evidence links a card shows before "+N more". */
const MAX_LINKS = 8;

function EvidenceLinks({
  seqs,
  bySeq,
  onEvidence,
}: {
  seqs: readonly number[];
  bySeq: ReadonlyMap<number, AuditEvent>;
  onEvidence: (seq: number) => void;
}) {
  const shown = seqs.slice(0, MAX_LINKS);
  return (
    <ul aria-label="Evidence" className="mt-1.5 flex flex-wrap gap-1.5 font-mono text-[11px]">
      {shown.map((seq) => {
        const event = bySeq.get(seq);
        const label = event
          ? `#${seq} ${CATEGORY_LABELS[event.category]}${event.site ? ` ${formatSite(event.site)}` : ""}`
          : `#${seq}`;
        return (
          <li key={seq}>
            <button
              type="button"
              data-testid="evidence-link"
              title={event ? describeEvent(event) : undefined}
              aria-label={`Show event ${seq} in the timeline${event ? `: ${describeEvent(event)}` : ""}`}
              onClick={() => onEvidence(seq)}
              className="rounded border border-border px-1 text-fg-dark hover:bg-bg-highlight hover:text-fg focus-visible:outline-2 focus-visible:outline-blue"
            >
              {label}
            </button>
          </li>
        );
      })}
      {seqs.length > MAX_LINKS && <li className="text-comment">+{seqs.length - MAX_LINKS} more</li>}
    </ul>
  );
}

function LayerTree({
  nodes,
  onOpenLayer,
}: {
  nodes: LayerNode[];
  onOpenLayer: (layer: number) => void;
}) {
  return (
    <ul className="space-y-1 border-l border-cat-codegen/50 pl-3 font-mono text-[13px]">
      {nodes.map(({ layer, children }) => {
        const firstLine = layer.code.split("\n").find((line) => line.trim() !== "") ?? "";
        return (
          <li key={layer.id} data-testid="layer-node">
            <div className="flex flex-wrap items-baseline gap-2">
              <button
                type="button"
                onClick={() => onOpenLayer(layer.id)}
                className="rounded text-cat-codegen underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-blue"
              >
                {layerLabel(layer)}
              </button>
              {layer.site && <span className="text-comment">from {formatSite(layer.site)}</span>}
              {layer.parseError && <span className="text-red1">didn't parse</span>}
            </div>
            <p className="truncate text-comment" title={firstLine}>
              {firstLine}
            </p>
            {children.length > 0 && <LayerTree nodes={children} onOpenLayer={onOpenLayer} />}
          </li>
        );
      })}
    </ul>
  );
}

function ManifestBlocks({ blocks }: { blocks: ManifestBlock[] }) {
  if (blocks.length === 0) return <Empty>The script needed no grants.</Empty>;
  return (
    <div className="space-y-2">
      {blocks.map((block) => (
        <CopyBlock key={block.id} block={block} />
      ))}
    </div>
  );
}

function CopyBlock({ block }: { block: ManifestBlock }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () => {
    navigator.clipboard?.writeText(block.text).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };
  return (
    <figure
      data-testid={`manifest-${block.id}`}
      className="rounded border border-border bg-bg-dark"
    >
      <figcaption className="flex items-center justify-between border-b border-border px-2 py-1 text-xs text-fg-dark">
        {block.title}
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${block.title}`}
          className="flex items-center gap-1 rounded px-1 hover:text-fg focus-visible:outline-2 focus-visible:outline-blue"
        >
          {copied ? (
            <Check aria-hidden className="size-3.5" />
          ) : (
            <Copy aria-hidden className="size-3.5" />
          )}
          <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
        </button>
      </figcaption>
      <pre className="whitespace-pre-wrap break-all px-2 py-1.5 font-mono text-[12px] text-fg">
        {block.text}
      </pre>
    </figure>
  );
}
